/* Toggl CSV import and Toggl-format export (brief section 7), ported from the prototype. Everything here is pure:
   it reads a file and the data already loaded, and returns what WOULD be written. db.ts does the writing.
   An entry's identity = person + exact start + exact end (+ occurrence number for truly identical rows), so the same
   Toggl row always gets the same id: 'i_' + cyrb53(<email-or-name>␟<start>␟<end>#<occurrence>). Re-importing the same
   or an overlapping file therefore never duplicates anything. */
import {BEGINNING, DEFAULT_COST, type Client, type Entry, type Member, type Project, type Task} from './model.ts';
import {offsetAtMs, startMsFor, wallAsUtc, wallOf, wallToMsList, parseDurationStr, parseTimeStr} from './time.ts';

const SEP = '␟';

/* ---------- CSV reading ---------- */
export interface Parsed { headers: string[]; rows: Record<string, string | number>[] }
export function parseCsv(text: string): Parsed {
  text = String(text).replace(/^﻿/, '');   // Toggl files start with a UTF-8 BOM
  const rows: string[][] = [];
  let row: string[] = [], field = '', inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i], next = text[i + 1];
    if (inQuotes) {
      if (c === '"' && next === '"') { field += '"'; i++; }
      else if (c === '"') inQuotes = false;
      else field += c;
    } else if (c === '"') inQuotes = true;
    else if (c === ',') { row.push(field); field = ''; }
    else if (c === '\r') { /* skip */ }
    else if (c === '\n') { row.push(field); rows.push(row); row = []; field = ''; }
    else field += c;
  }
  if (field.length || row.length) { row.push(field); rows.push(row); }
  const filtered = rows.filter(r => r.length > 1 || (r.length === 1 && r[0] !== ''));
  if (!filtered.length) return {headers: [], rows: []};
  const headers = filtered[0].map(h => h.replace(/^﻿/, '').trim());
  return {headers, rows: filtered.slice(1).map((r, idx) => {
    const obj: Record<string, string | number> = {__line: idx + 2};
    headers.forEach((h, i) => { obj[h] = r[i] !== undefined ? r[i] : ''; });
    return obj;
  })};
}

export const IMPORT_FIELDS = [
  {key: 'member', label: 'User', guesses: ['user', 'username', 'team member']},
  {key: 'email', label: 'Email', guesses: ['email', 'user email']},
  {key: 'client', label: 'Client', guesses: ['client']},
  {key: 'project', label: 'Project', guesses: ['project']},
  {key: 'task', label: 'Task', guesses: ['task']},
  {key: 'description', label: 'Description', guesses: ['description', 'task description', 'notes']},
  {key: 'billable', label: 'Billable', guesses: ['billable']},
  {key: 'startDate', label: 'Start date', guesses: ['start date']},
  {key: 'startTime', label: 'Start time', guesses: ['start time']},
  {key: 'endDate', label: 'End date', guesses: ['end date']},
  {key: 'endTime', label: 'End time', guesses: ['end time']},
  {key: 'duration', label: 'Duration', guesses: ['duration']},
  {key: 'tags', label: 'Tags', guesses: ['tags']},
] as const;
export type Mapping = Record<string, string>;
export function guessMapping(headers: string[]): Mapping {
  const map: Mapping = {}, lower = headers.map(h => h.toLowerCase().trim());
  IMPORT_FIELDS.forEach(f => {
    let found = '';
    for (const g of f.guesses) { const idx = lower.indexOf(g); if (idx >= 0) { found = headers[idx]; break; } }
    map[f.key] = found;
  });
  return map;
}
export const mappingComplete = (m: Mapping) => ['startDate', 'startTime', 'duration', 'member'].every(k => m[k]);

/** strict: an unreadable date is an error, never "today" */
export function parseDateStr(str: string | null | undefined): string | null {
  if (!str) return null;
  str = String(str).trim();
  let y: number, mo: number, d: number, m: RegExpMatchArray | null;
  if ((m = str.match(/^(\d{4})-(\d{2})-(\d{2})$/))) { y = +m[1]; mo = +m[2]; d = +m[3]; }
  else if ((m = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/))) { mo = +m[1]; d = +m[2]; y = +m[3]; }   // US M/D/YYYY
  else return null;
  const dt = new Date(Date.UTC(y, mo - 1, d));
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== mo - 1 || dt.getUTCDate() !== d) return null;
  return y + '-' + String(mo).padStart(2, '0') + '-' + String(d).padStart(2, '0');
}

/** small deterministic 53-bit hash -> stable entry ids (identical to the prototype; ids from backups depend on it) */
export function cyrb53(str: string, seed = 0): number {
  let h1 = 0xdeadbeef ^ seed, h2 = 0x41c6ce57 ^ seed;
  for (let i = 0; i < str.length; i++) { const ch = str.charCodeAt(i); h1 = Math.imul(h1 ^ ch, 2654435761); h2 = Math.imul(h2 ^ ch, 1597334677); }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return 4294967296 * (2097151 & h2) + (h1 >>> 0);
}
export const importId = (userKey: string, startedAt: string, endedAt: string, occ: number) => 'i_' + cyrb53([userKey, startedAt, endedAt].join(SEP) + '#' + occ).toString(36);

/** pick the right reading of an ambiguous start (clocks repeated in the fall) using the known duration */
export function resolveSpan(startWall: string, endWall: string | null, dur: number) {
  const c = wallToMsList(startWall);
  let pick = c.length ? c[0] : startMsFor(startWall), ok = true;
  if (endWall && c.length) {
    const hit = c.find(ms => Math.abs(wallAsUtc(wallOf(ms + dur * 1000)) - wallAsUtc(endWall)) <= 2000);
    if (hit !== undefined) pick = hit; else ok = false;
  } else if (endWall) ok = false;
  return {startMs: pick, so: offsetAtMs(pick), ok, dst: offsetAtMs(pick) !== offsetAtMs(pick + dur * 1000)};
}

/* ---------- analysis: reads the file, touches no data ---------- */
export interface Row {
  id: string; line: number; slot: string; occ: number; userKey: string; userName: string; email: string;
  clientName: string; projectName: string; taskName: string; description: string; billable: boolean; tags: string[];
  date: string; startedAt: string; endedAt: string; durationSeconds: number; so: number; startMs: number;
}
export interface FileUser { key: string; name: string; email: string; rows: number; seconds: number }
export interface Analysis {
  rows: Row[]; errors: {line: number; msg: string; refused?: boolean; src?: string}[]; users: FileUser[];   // refused: the database would refuse it (014); src: the file's row
  stats: {
    rowsRead: number; seconds: number; billableSeconds: number; minDate: string | null; maxDate: string | null;
    long12: {line: number; user: string; date: string; hours: number}[]; zero: number; blankClient: number; blankProject: number;
    trimmed: number; dupExtra: number; overlaps: number; mismatch: number; dst: number;
    tags: Record<string, number>; clients: Set<string>; projects: Set<string>; tasks: Set<string>;
  };
}
export function analyzeCsv(parsed: Parsed, mapping: Mapping): Analysis {
  const g = (r: Record<string, unknown>, k: string) => mapping[k] ? String(r[mapping[k]] ?? '') : '';
  const rows: Row[] = [], errors: Analysis['errors'] = [], occ: Record<string, number> = {}, userMap: Record<string, FileUser> = {};
  const stats: Analysis['stats'] = {rowsRead: 0, seconds: 0, billableSeconds: 0, minDate: null, maxDate: null, long12: [], zero: 0, blankClient: 0,
    blankProject: 0, trimmed: 0, dupExtra: 0, overlaps: 0, mismatch: 0, dst: 0, tags: {}, clients: new Set(), projects: new Set(), tasks: new Set()};
  for (const r of parsed.rows) {
    if (Object.keys(r).every(k => k === '__line' || String(r[k]).trim() === '')) continue;
    stats.rowsRead++;
    const line = Number(r.__line);
    const userName = g(r, 'member').trim(), email = g(r, 'email').trim().toLowerCase();
    const sd = parseDateStr(g(r, 'startDate')), st = parseTimeStr(g(r, 'startTime') || '00:00:00');
    const ed = parseDateStr(g(r, 'endDate') || g(r, 'startDate')), et = parseTimeStr(g(r, 'endTime') || g(r, 'startTime') || '00:00:00');
    if (!userName && !email) { errors.push({line, msg: 'no user or email'}); continue; }
    if (!sd) { errors.push({line, msg: 'unreadable start date "' + g(r, 'startDate') + '"'}); continue; }
    if (!st) { errors.push({line, msg: 'unreadable start time "' + g(r, 'startTime') + '"'}); continue; }
    const startedAt = sd + 'T' + st;
    // a start the Chicago clocks skipped (spring forward) is no real time: the database refuses it (migration 014), so it is
    // reported here with its line, like any unreadable row, and nothing else of the file is held up by it
    if (!wallToMsList(startedAt).length) { errors.push({line, msg: 'start ' + sd + ' ' + st + ' does not exist in Central time (the clocks skip that hour in spring)', refused: true,
      src: [userName || email, g(r, 'startDate') + ' ' + g(r, 'startTime'), g(r, 'endDate') + ' ' + g(r, 'endTime'), g(r, 'duration'), g(r, 'client'), g(r, 'project'), g(r, 'description')].map(x => String(x).trim()).join(' | ')}); continue; }
    let endedAt = ed && et ? ed + 'T' + et : null;
    let dur = parseDurationStr(g(r, 'duration'));
    if (dur == null && endedAt) { const calc = Math.round((startMsFor(endedAt) - startMsFor(startedAt)) / 1000); if (isFinite(calc) && calc >= 0) dur = calc; }
    if (dur == null) { errors.push({line, msg: 'unreadable duration "' + g(r, 'duration') + '"'}); continue; }
    const span = resolveSpan(startedAt, endedAt, dur);
    if (endedAt && !span.ok) stats.mismatch++;
    if (span.dst) stats.dst++;
    if (!endedAt) endedAt = wallOf(span.startMs + dur * 1000);

    const raw = {c: g(r, 'client'), p: g(r, 'project'), t: g(r, 'task'), d: g(r, 'description')};
    const client = raw.c.trim(), project = raw.p.trim(), task = raw.t.trim(), description = raw.d.trim();
    if (raw.c !== client || raw.p !== project || raw.t !== task || raw.d !== description) stats.trimmed++;
    const clientName = client || '(No client)', projectName = project || '(No project)';
    if (!client) stats.blankClient++;
    if (!project) stats.blankProject++;
    const bl = g(r, 'billable').trim().toLowerCase();
    const billable = bl ? (bl === 'yes' || bl === 'true' || bl === '1' || bl === 'y') : true;
    const tags = g(r, 'tags').split(',').map(t => t.trim()).filter(Boolean);
    tags.forEach(t => { stats.tags[t] = (stats.tags[t] || 0) + 1; });

    const userKey = email || 'name:' + userName.toLowerCase();
    const base = [userKey, startedAt, endedAt].join(SEP);      // who + exact start + exact end
    occ[base] = (occ[base] || 0) + 1;
    if (occ[base] > 1) stats.dupExtra++;
    const u = userMap[userKey] ??= {key: userKey, name: userName, email, rows: 0, seconds: 0};
    u.rows++; u.seconds += dur;
    stats.seconds += dur; if (billable) stats.billableSeconds += dur;
    if (dur === 0) stats.zero++;
    if (dur > 12 * 3600) stats.long12.push({line, user: userName, date: sd, hours: dur / 3600});
    if (!stats.minDate || sd < stats.minDate) stats.minDate = sd;
    if (!stats.maxDate || sd > stats.maxDate) stats.maxDate = sd;
    stats.clients.add(clientName); stats.projects.add(clientName + SEP + projectName);
    if (task) stats.tasks.add(clientName + SEP + projectName + SEP + task);
    rows.push({id: importId(userKey, startedAt, endedAt, occ[base]), line, slot: startedAt + '|' + endedAt, occ: occ[base], userKey, userName, email,
      clientName, projectName, taskName: task, description, billable, tags, date: sd, startedAt, endedAt, durationSeconds: dur, so: span.so, startMs: span.startMs});
  }
  // overlaps per person (Toggl allows them; informational). Real instants, so daylight-saving nights can't fake one.
  const byUser: Record<string, Row[]> = {};
  rows.forEach(r => (byUser[r.userKey] ??= []).push(r));
  Object.values(byUser).forEach(list => {
    list.sort((a, b) => a.startMs - b.startMs);
    let reach = -Infinity;
    for (const x of list) { if (x.startMs < reach - 1000 && x.durationSeconds > 0) stats.overlaps++; reach = Math.max(reach, x.startMs + x.durationSeconds * 1000); }
  });
  return {rows, errors, stats, users: Object.values(userMap)};
}

/* ---------- who is who: a file's person -> a team member (by remembered aliases, then e-mail, then a unique name) ---------- */
const normKey = (s: string) => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
export function autoMatchMember(u: {email: string; name: string}, members: Member[]): string {
  const email = (u.email || '').toLowerCase(), nm = (u.name || '').toLowerCase();
  let m = members.find(x => x.importAliases.some(a => (email && a === email) || (nm && a === 'name:' + nm)));
  if (m) return m.id;
  m = members.find(x => email && (x.email || '').toLowerCase() === email);
  if (m) return m.id;
  const n = normKey(u.name), same = members.filter(x => n && normKey(x.name) === n);
  return same.length === 1 ? same[0].id : '';
}
export const NEW_MEMBER = '__new__', NEW_FORMER = '__new_inactive__';
export const isNewChoice = (v: string) => v === NEW_MEMBER || v === NEW_FORMER;

/** what is already stored, keyed the way the importer recognizes entries */
export interface Existing { ids: Set<string>; slots: Map<string, {durationSeconds: number; description: string; billable: boolean}[]>; total: number }
export function indexExisting(entries: Pick<Entry, 'id' | 'memberId' | 'startedAt' | 'endedAt' | 'durationSeconds' | 'description' | 'billable'>[]): Existing {
  const ids = new Set<string>(), slots: Existing['slots'] = new Map();
  for (const e of entries) {
    ids.add(e.id);
    const k = e.memberId + '|' + e.startedAt + '|' + e.endedAt, l = slots.get(k);
    const v = {durationSeconds: e.durationSeconds, description: e.description || '', billable: !!e.billable};
    if (l) l.push(v); else slots.set(k, [v]);
  }
  return {ids, slots, total: entries.length};
}
/** same entry = same id, or the same person already has this many entries with exactly this start and end */
export const isStored = (r: Row, memberId: string, ex: Existing) => ex.ids.has(r.id) || (ex.slots.get(memberId + '|' + r.slot)?.length || 0) >= r.occ;

export function matchStats(a: Analysis, choice: Record<string, string>, ex: Existing, existingDates: {memberId: string; date: string}[]) {
  let already = 0; const mapped = new Set<string>(); let mappedRows = 0;
  for (const r of a.rows) {
    const m = choice[r.userKey];
    if (!m || isNewChoice(m)) continue;
    mappedRows++; mapped.add(m);
    if (isStored(r, m, ex)) already++;
  }
  let existingInRange = 0;
  if (mapped.size && a.stats.minDate) for (const e of existingDates) if (mapped.has(e.memberId) && e.date >= a.stats.minDate && e.date <= a.stats.maxDate!) existingInRange++;
  // a file that covers dates where these people already have many entries, but few match exactly (e.g. exported again in
  // another time zone) would create duplicates: block until the person confirms
  const suspicious = existingInRange > 0 && already < 0.5 * Math.min(existingInRange, mappedRows);
  return {already, fresh: a.rows.length - already, existingInRange, suspicious};
}

/* ---------- names: clients match by current name or any earlier name (alias) ---------- */
export const lc = (s: string) => String(s || '').trim().toLowerCase();
export function clientIndex(clients: Pick<Client, 'id' | 'name' | 'aliases'>[]): Map<string, string> {
  const m = new Map<string, string>();
  for (const c of clients) for (const k of [c.name, ...(c.aliases || [])].map(lc).filter(Boolean)) if (!m.has(k) || k === lc(c.name)) m.set(k, c.id);
  return m;
}

/* ---------- the write plan: exactly what an import will add, computed from a fresh read of the database ---------- */
export interface Structure { members: Member[]; clients: Client[]; projects: Project[]; tasks: Task[] }
export interface ImportPlan {
  newMembers: {id: string; name: string; email: string; role: 'member' | 'archived'; active: boolean; import_aliases: string[]}[];
  memberUpdates: {id: string; import_aliases: string[]; email?: string}[];
  clients: {id: string; name: string; active: boolean}[];
  projects: {id: string; client_id: string; name: string; active: boolean}[];
  tasks: {id: string; project_id: string; name: string}[];
  entries: ReturnType<typeof importRowToEntry>[];
  memberIdByUser: Record<string, string>;
  already: number; differ: number;
}
export const NEW_MEMBER_COST = {effective_from: BEGINNING, rate: DEFAULT_COST};
export function importRowToEntry(r: Row, memberId: string, projectId: string, taskId: string | null) {
  return {id: r.id, member_id: memberId, project_id: projectId, task_id: taskId, description: r.description, billable: r.billable, work_date: r.date,
          started_wall: r.startedAt, ended_wall: r.endedAt, utc_offset_min: r.so, duration_seconds: r.durationSeconds, tags: r.tags, source: 'import'};
}
export function planImport(a: Analysis, choice: Record<string, string>, s: Structure, ex: Existing, newId: (prefix: string) => string): ImportPlan {
  const plan: ImportPlan = {newMembers: [], memberUpdates: [], clients: [], projects: [], tasks: [], entries: [], memberIdByUser: {}, already: 0, differ: 0};
  for (const u of a.users) {
    let mid = choice[u.key];
    if (!mid) throw new Error('Choose a team member for ' + (u.name || u.email));
    const aliases = [u.email, u.name ? 'name:' + u.name.toLowerCase() : ''].filter(Boolean);
    if (isNewChoice(mid)) {
      const former = mid === NEW_FORMER;
      mid = newId('m_');
      plan.newMembers.push({id: mid, name: u.name || u.email, email: u.email || '', role: former ? 'archived' : 'member', active: !former, import_aliases: aliases});
    } else {
      const m = s.members.find(x => x.id === mid);
      if (!m) throw new Error('That team member no longer exists — reload and choose again.');
      const union = Array.from(new Set([...m.importAliases, ...aliases]));
      const upd: ImportPlan['memberUpdates'][number] = {id: mid, import_aliases: union};
      if (!m.email && u.email) upd.email = u.email;
      if (union.length !== m.importAliases.length || upd.email) plan.memberUpdates.push(upd);
    }
    plan.memberIdByUser[u.key] = mid;
  }
  const cIdx = clientIndex(s.clients), pIdx = new Map<string, string>(), tIdx = new Map<string, string>();
  s.projects.forEach(p => pIdx.set(p.clientId + SEP + lc(p.name), p.id));
  s.tasks.forEach(t => tIdx.set(t.projectId + SEP + lc(t.name), t.id));
  const clientId = (name: string) => {
    let id = cIdx.get(lc(name));
    if (!id) { id = newId('c_'); cIdx.set(lc(name), id); plan.clients.push({id, name: name.trim(), active: true}); }
    return id;
  };
  const projectId = (cname: string, pname: string) => {
    const cid = clientId(cname), k = cid + SEP + lc(pname);
    let id = pIdx.get(k);
    if (!id) { id = newId('p_'); pIdx.set(k, id); plan.projects.push({id, client_id: cid, name: pname.trim(), active: true}); }  // no rate yet (NULL), never 0
    return id;
  };
  const taskId = (pid: string, tname: string) => {
    const k = pid + SEP + lc(tname);
    let id = tIdx.get(k);
    if (!id) { id = newId('t_'); tIdx.set(k, id); plan.tasks.push({id, project_id: pid, name: tname.trim()}); }
    return id;
  };
  for (const r of a.rows) {
    const mid = plan.memberIdByUser[r.userKey];
    if (isStored(r, mid, ex)) {
      plan.already++;
      // stored but different (edited here or in Toggl since): reported, left as it is
      const same = ex.slots.get(mid + '|' + r.slot)?.some(o => o.durationSeconds === r.durationSeconds && o.description === r.description && o.billable === r.billable);
      if (!same) plan.differ++;
      continue;
    }
    const pid = projectId(r.clientName, r.projectName);
    plan.entries.push(importRowToEntry(r, mid, pid, r.taskName ? taskId(pid, r.taskName) : null));
  }
  return plan;
}

/** after writing: every row of the file must now exist exactly once (matched by person + exact start + end) */
export function verifyImport(a: Analysis, memberIdByUser: Record<string, string>, after: Existing) {
  let present = 0, secPresent = 0; const used: Record<string, number> = {};
  for (const r of a.rows) {
    const k = memberIdByUser[r.userKey] + '|' + r.slot, n = (used[k] = (used[k] || 0) + 1);
    const ex = after.slots.get(k)?.[n - 1];
    if (ex) { present++; secPresent += ex.durationSeconds; }
  }
  return {present, secPresent};
}

/* ---------- Toggl workspace file (clients, projects with rates, tasks, people's billing rates) ---------- */
export interface Workspace {
  format: 'october-toggl-workspace-v1';
  members?: {email: string; name: string; billRate: number | null}[];
  clients: string[];
  projects: {client: string; name: string; billableRate?: number | null; defaultBillable?: boolean; active?: boolean; startDate?: string | null}[];
  tasks: {client: string; project: string; name: string; active?: boolean}[];
}
export function readWorkspace(text: string): Workspace {
  let d: any; try { d = JSON.parse(text); } catch { throw new Error('That file is not valid JSON.'); }
  if (!d || d.format !== 'october-toggl-workspace-v1' || !Array.isArray(d.projects) || !Array.isArray(d.clients) || !Array.isArray(d.tasks))
    throw new Error('Not a Toggl workspace file prepared for this tracker.');
  return d;
}
/** Nothing existing is overwritten: missing clients/projects/tasks are added; an existing project only receives a rate
    if it has none (NULL), never over a rate that is set (including $0); a person only receives a billing rate if they have none. */
export function planWorkspace(d: Workspace, s: Structure, choice: Record<string, string>, newId: (prefix: string) => string) {
  const cIdx = clientIndex(s.clients), pIdx = new Map<string, Project>(), tHave = new Set<string>();
  s.projects.forEach(p => pIdx.set(p.clientId + SEP + lc(p.name), p));
  s.tasks.forEach(t => tHave.add(t.projectId + SEP + lc(t.name)));
  const clients: {id: string; name: string; active: boolean}[] = [];
  const projects: {id: string; client_id: string; name: string; default_billable: boolean; active: boolean; start_date: string | null}[] = [];
  const rates: {project_id: string; bill_rate: number}[] = [], fills: {project_id: string; bill_rate: number}[] = [];
  const tasks: {id: string; project_id: string; name: string; active: boolean}[] = [];
  const cid = (name: string) => { let id = cIdx.get(lc(name)); if (!id) { id = newId('c_'); cIdx.set(lc(name), id); clients.push({id, name: name.trim(), active: true}); } return id; };
  d.clients.forEach(c => { if (lc(c)) cid(c); });
  for (const p of d.projects) {
    const k = cid(p.client) + SEP + lc(p.name), ex = pIdx.get(k), rate = p.billableRate === undefined ? null : p.billableRate;
    if (!ex) {
      const id = newId('p_');
      pIdx.set(k, {id, clientId: cid(p.client), name: p.name.trim(), billableRate: rate, defaultBillable: !!p.defaultBillable, active: p.active !== false, startDate: p.startDate || null});
      projects.push({id, client_id: cid(p.client), name: p.name.trim(), default_billable: !!p.defaultBillable, active: p.active !== false, start_date: p.startDate || null});
      if (rate !== null) rates.push({project_id: id, bill_rate: rate});           // $0 is kept as $0; no rate stays no row
    } else if (ex.billableRate === null && rate !== null && !fills.some(f => f.project_id === ex.id)) fills.push({project_id: ex.id, bill_rate: rate});
  }
  for (const t of d.tasks) {
    const p = pIdx.get(cid(t.client) + SEP + lc(t.project));
    if (!p || tHave.has(p.id + SEP + lc(t.name))) continue;
    tHave.add(p.id + SEP + lc(t.name));
    tasks.push({id: newId('t_'), project_id: p.id, name: t.name.trim(), active: t.active !== false});
  }
  const people: {id: string; import_aliases: string[]; email?: string; billRate?: number}[] = [];
  for (const m of d.members || []) {
    const mid = choice[m.email]; if (!mid) continue;
    const cur = s.members.find(x => x.id === mid); if (!cur) continue;
    const p: (typeof people)[number] = {id: mid, import_aliases: Array.from(new Set([...cur.importAliases, lc(m.email), m.name ? 'name:' + m.name.toLowerCase() : ''].filter(Boolean)))};
    if (!cur.email && m.email) p.email = m.email;
    if (cur.billRate === null && m.billRate != null) p.billRate = m.billRate;
    if (p.import_aliases.length !== cur.importAliases.length || p.email || p.billRate !== undefined) people.push(p);
  }
  return {clients, projects, rates, fills, tasks, people,
          zero: d.projects.filter(p => p.billableRate === 0).length, rated: d.projects.filter(p => (p.billableRate ?? 0) > 0).length};
}

/* ---------- exports ---------- */
export function csvCell(v: unknown, always = false): string { const s = String(v == null ? '' : v); return (always || /[",\n\r]/.test(s)) ? '"' + s.replace(/"/g, '""') + '"' : s; }
const togglDuration = (sec: number) => { sec = Math.max(0, Math.round(sec || 0)); return Math.floor(sec / 60) + ':' + String(sec % 60).padStart(2, '0') + ' min'; };
export interface Names { member: (id: string) => Member | undefined; project: (id: string) => Project | undefined; client: (id: string) => Client | undefined; task: (id: string) => Task | undefined }
export function namesFrom(s: Structure): Names {
  const m = new Map(s.members.map(x => [x.id, x])), p = new Map(s.projects.map(x => [x.id, x])), c = new Map(s.clients.map(x => [x.id, x])), t = new Map(s.tasks.map(x => [x.id, x]));
  return {member: id => m.get(id), project: id => p.get(id), client: id => c.get(id), task: id => t.get(id)};
}
export const TOGGL_HEADERS = ['User', 'Email', 'Client', 'Project', 'Task', 'Description', 'Billable', 'Start date', 'Start time', 'End date', 'End time', 'Duration', 'Tags'];
/** the same 13 columns, order and duration style as a Toggl export, so the file can be imported again (here or elsewhere) */
export function entriesToTogglCsv(entries: Entry[], n: Names): string {
  const lines = [TOGGL_HEADERS.map(h => csvCell(h, true)).join(',')];
  [...entries].sort((a, b) => b.startedAt.localeCompare(a.startedAt) || a.id.localeCompare(b.id)).forEach(e => {
    const p = n.project(e.projectId), m = n.member(e.memberId), c = p ? n.client(p.clientId) : undefined;
    lines.push([m?.name ?? '', m?.email ?? '', c?.name ?? '', p?.name ?? '', n.task(e.taskId)?.name ?? '', e.description, e.billable ? 'Yes' : 'No',
      e.startedAt.slice(0, 10), e.startedAt.slice(11, 19), e.endedAt.slice(0, 10), e.endedAt.slice(11, 19), togglDuration(e.durationSeconds), e.tags.join(', ')]
      .map(v => csvCell(v, true)).join(','));
  });
  return '﻿' + lines.join('\r\n');
}
